import React from 'react';
import {useLocation} from 'react-router-dom';

import {NavLink} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Tooltip} from 'src/design/atoms/Tooltip';
import type {
  FeatureFlagEnum,
  ModuleEnum,
  RoleEnum} from 'src/api/ProactApiClient';
import {
  PermissionOperationEnum,
  UserStatusEnum,
} from 'src/api/ProactApiClient';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {FeatureFlagGuard} from 'src/app/auth/FeatureFlagGuard';
import {useAppSelector} from 'src/redux/hooks';
import {useVisibleSidebarLinks} from 'src/app/layout/Sidebar/useVisibleSidebarLinks';
import {isOfflineRoute} from 'src/offline/offlineRoutes';
import {useConnectivity} from 'src/offline/useConnectivity';
import classes from './SidebarLinks.module.css';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface SidebarLinkItemProps {
  name: string;
  path: string;
  validActivePaths?: string[];
  icon: any;
  roles?: RoleEnum[];
  module?: ModuleEnum;
  operation?: PermissionOperationEnum;
  operationName?: string;
  featureFlag?: FeatureFlagEnum;
  allowPendingUsers?: boolean;
  isDivider?: boolean;
  subLinks?: SidebarLinkItemProps[];
  allowGlobalAdminBypass?: boolean;
  superAdminOnly?: boolean;
  /** Pages listed on the directory page this link opens; the link hides when none is visible. */
  directory?: DirectoryCategory[];
  /** Icon-only rendering for the collapsed 64px rail; the name moves to a tooltip. */
  compact?: boolean;
}

export interface DirectoryEntry {
  link: SidebarLinkItemProps;
  description: string;
}

export interface DirectoryCategory {
  title: string;
  entries: DirectoryEntry[];
}

export const directoryLinks = (
  directory: DirectoryCategory[]
): SidebarLinkItemProps[] =>
  directory.flatMap(category => category.entries.map(entry => entry.link));

const SidebarLinkItem: React.FC<SidebarLinkItemProps> = ({
  name,
  path,
  validActivePaths,
  icon,
  module,
  operation = PermissionOperationEnum.View,
  operationName,
  roles,
  featureFlag,
  isDivider,
  subLinks,
  allowPendingUsers,
  allowGlobalAdminBypass,
  superAdminOnly,
  directory,
  compact,
}) => {
  const location = useLocation();
  const navigate = useAppNavigate();
  const {session, isSuperAdmin} = useAppSelector(state => state.session);
  const {isOffline} = useConnectivity();
  const [open, setOpen] = React.useState(false);
  const visibleDirectoryLinks = useVisibleSidebarLinks(
    directory ? directoryLinks(directory) : []
  );

  const activePaths = React.useMemo(
    () =>
      validActivePaths ??
      (directory
        ? [
            path,
            ...directoryLinks(directory).flatMap(
              l => l.validActivePaths ?? [l.path]
            ),
          ]
        : [path]),
    [validActivePaths, directory, path]
  );

  const isActive = React.useMemo(() => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    // Segment-boundary match so sibling routes sharing a prefix (e.g.
    // /assistant vs /assistant-control-center) do not both highlight.
    return activePaths.some(
      p => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
  }, [location.pathname, path, activePaths]);

  if (superAdminOnly && !isSuperAdmin) {
    return null;
  }
  if (directory && visibleDirectoryLinks.length === 0) {
    return null;
  }
  if (
    roles &&
    !isSuperAdmin &&
    session?.user?.roleId &&
    !roles.some(role => role === session.user?.roleId)
  ) {
    return null;
  }
  if (
    !isSuperAdmin &&
    !allowPendingUsers &&
    session?.userStatus === UserStatusEnum.Pending
  ) {
    return null;
  }
  if (isDivider) {
    return <Divider my="sm" color="dark.5" />;
  }

  // The rail has no room for labels; sub-links collapse into their parent link.
  if (compact) {
    return (
      <FeatureFlagGuard requiredFeatureFlag={featureFlag}>
        <PermissionGuard
          module={module}
          operation={operation}
          operationName={operationName}
          allowGlobalAdminBypass={allowGlobalAdminBypass}>
          <Tooltip label={name} position="right" withArrow>
            <NavLink
              aria-label={name}
              leftSection={icon}
              active={isActive}
              disabled={isOffline && !isOfflineRoute(path)}
              onClick={() => navigate(path)}
              classNames={{
                root: classes.compactNavLink,
                body: classes.compactNavLinkBody,
                section: classes.compactNavLinkSection,
              }}
            />
          </Tooltip>
        </PermissionGuard>
      </FeatureFlagGuard>
    );
  }

  if (subLinks) {
    return (
      <FeatureFlagGuard requiredFeatureFlag={featureFlag}>
        <PermissionGuard
          module={module}
          operation={operation}
          operationName={operationName}
          allowGlobalAdminBypass={allowGlobalAdminBypass}>
          <NavLink
            label={name}
            leftSection={icon}
            opened={open}
            onChange={setOpen}
            classNames={{
              root: classes.navLink,
              label: classes.label,
              section: classes.section,
            }}>
            {subLinks.map((sublink, index) => {
              const isSubLinkActive =
                location.pathname === sublink.path ||
                location.pathname.startsWith(`${sublink.path}/`);

              if (
                sublink.roles &&
                !isSuperAdmin &&
                session?.user?.roleId &&
                !sublink.roles.some(role => role === session.user?.roleId)
              ) {
                return null;
              }
              if (
                !isSuperAdmin &&
                !sublink.allowPendingUsers &&
                session?.userStatus === UserStatusEnum.Pending
              ) {
                return null;
              }

              return (
                <FeatureFlagGuard
                  key={index}
                  requiredFeatureFlag={sublink.featureFlag}>
                  <PermissionGuard
                    module={sublink.module}
                    operation={sublink.operation ?? PermissionOperationEnum.View}
                    operationName={sublink.operationName}
                    allowGlobalAdminBypass={allowGlobalAdminBypass}>
                    <NavLink
                      label={sublink.name}
                      leftSection={sublink.icon}
                      active={isSubLinkActive}
                      disabled={isOffline && !isOfflineRoute(sublink.path)}
                      onClick={() => navigate(sublink.path)}
                      classNames={{
                        root: classes.childNavLink,
                        label: classes.label,
                        section: classes.section,
                      }}
                    />
                  </PermissionGuard>
                </FeatureFlagGuard>
              );
            })}
          </NavLink>
        </PermissionGuard>
      </FeatureFlagGuard>
    );
  }

  return (
    <FeatureFlagGuard requiredFeatureFlag={featureFlag}>
      <PermissionGuard
        module={module}
        operation={operation}
        operationName={operationName}
        allowGlobalAdminBypass={allowGlobalAdminBypass}>
        <NavLink
          label={name}
          leftSection={icon}
          active={isActive}
          disabled={isOffline && !isOfflineRoute(path)}
          onClick={() => navigate(path)}
          classNames={{
            root: classes.navLink,
            label: classes.label,
            section: classes.section,
          }}
        />
      </PermissionGuard>
    </FeatureFlagGuard>
  );
};

export interface SidebarLinksProps {
  links: SidebarLinkItemProps[];
  /** Icon-only rendering for the collapsed 64px rail. */
  compact?: boolean;
}

export const SidebarLinks: React.FC<SidebarLinksProps> = ({links, compact}) => {
  return (
    <>
      {links.map((link: SidebarLinkItemProps, index) => (
        <SidebarLinkItem key={index} {...link} compact={compact} />
      ))}
    </>
  );
};
